import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { titleRepositorySingleton } from '@warehouse/title/infra';
import { useStateQuery } from '@warehouse/shared/util';

export function useDeleteTitle() {
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const [, setSettingsTab] = useStateQuery<string>({
		queryName: 'settings',
		type: 'string',
	});
	const titleRepo = titleRepositorySingleton.get();

	const deleteTitle = useCallback(
		async (uuid?: string) => {
			if (!uuid) {
				enqueueSnackbar('Cannot delete title: UUID missing', { variant: 'error' });
				return;
			}

			setLoading(true);
			setErrors([]);

			try {
				await titleRepo.deleteTitle(uuid);
				enqueueSnackbar(`Title ${uuid} deleted successfully`, { variant: 'success' });
				setSettingsTab(null);
				navigate('/titles');
			} catch (err: any) {
				const errorList: string[] = [];

				if (Array.isArray(err?.graphQLErrors) && err.graphQLErrors.length > 0) {
					err.graphQLErrors.forEach((e: any) => {
						if (Array.isArray(e?.extensions?.errors) && e.extensions.errors.length > 0) {
							errorList.push(...e.extensions.errors);
						} else if (e.message) {
							errorList.push(e.message);
						}
					});
				}
				if (errorList.length === 0) {
					errorList.push(err?.message || 'Failed to delete title');
				}
				setErrors(errorList);
				errorList.forEach((error) => {
					enqueueSnackbar(error, { variant: 'error' });
				});
			} finally {
				setLoading(false);
			}
		},
		[navigate, titleRepo, enqueueSnackbar, setSettingsTab],
	);

	const clearErrors = useCallback(() => setErrors([]), []);

	return { deleteTitle, loading, errors, clearErrors };
}
